import React, { useEffect, useRef, useState } from 'react';
import { createChart } from 'lightweight-charts';
import { Box, CircularProgress, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material';
import { apiRequest } from '../utils/api';

const RANGES = [
  { key: '3mo', label: '3M' },
  { key: '6mo', label: '6M' },
  { key: 'ytd', label: 'YTD' },
  { key: '1y', label: '1Y' },
];

const toDay = (d) => {
  if (!d) return null;
  const s = String(d);
  return s.length >= 10 ? s.slice(0, 10) : s;
};

/**
 * Candlestick chart with trade execution markers (B/S) and open-position
 * cost-basis price lines. Falls back to a line series when the candles lack
 * OHLC data (e.g. Robinhood-sourced history).
 */
export default function StockTradeChart({ symbol, trades = [], positions = [], height = 380 }) {
  const theme = useTheme();
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const [range, setRange] = useState('1y');
  const [interval, setInterval] = useState('1d');
  const [candles, setCandles] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!symbol) return;
    let cancelled = false;
    setLoading(true);
    apiRequest(`/api/stocks/${encodeURIComponent(symbol)}/history?range=${range}&interval=${interval}`)
      .then(resp => { if (!cancelled) setCandles(resp.data?.candles || []); })
      .catch(() => { if (!cancelled) setCandles([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [symbol, range, interval]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || candles.length === 0) return undefined;

    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
    }

    const isDark = theme.palette.mode === 'dark';
    const chart = createChart(el, {
      height,
      width: el.clientWidth,
      layout: {
        background: { type: 'solid', color: 'transparent' },
        textColor: theme.palette.text.secondary,
      },
      grid: {
        vertLines: { color: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' },
        horzLines: { color: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' },
      },
      rightPriceScale: { borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)' },
      timeScale: { borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)' },
      crosshair: { mode: 0 },
    });
    chartRef.current = chart;

    const hasOhlc = candles.some(c => c.open != null && c.high != null && c.low != null);
    let series;
    if (hasOhlc) {
      series = chart.addCandlestickSeries({
        upColor: '#2e7d32', downColor: '#c62828',
        borderUpColor: '#2e7d32', borderDownColor: '#c62828',
        wickUpColor: '#2e7d32', wickDownColor: '#c62828',
      });
      series.setData(candles.map(c => ({
        time: c.date, open: c.open, high: c.high, low: c.low, close: c.close,
      })));
    } else {
      series = chart.addLineSeries({ color: '#1976d2', lineWidth: 2 });
      series.setData(candles.map(c => ({ time: c.date, value: c.close })));
    }

    // Trade markers — buys below the bar, sells above. Dedupe per date+action.
    const seen = new Set();
    const markers = [];
    (trades || []).forEach(t => {
      const action = String(t.action || t.tradeType || t.trade_type || '').toUpperCase();
      const isSell = /SELL|STC|SHORT|SOLD/.test(action);
      const entry = toDay(t.tradeDate || t.trade_date);
      const exit = toDay(t.closingDate || t.closing_date);
      const entries = [[entry, isSell ? 'S' : 'B'], [exit, isSell ? 'B' : 'S']];
      entries.forEach(([time, kind]) => {
        if (!time) return;
        const key = `${time}|${kind}`;
        if (seen.has(key)) return;
        seen.add(key);
        markers.push({
          time,
          position: kind === 'B' ? 'belowBar' : 'aboveBar',
          color: kind === 'B' ? '#2e7d32' : '#c62828',
          shape: kind === 'B' ? 'arrowUp' : 'arrowDown',
          text: kind,
        });
      });
    });
    markers.sort((a, b) => (a.time < b.time ? -1 : 1));
    const candleDates = new Set(candles.map(c => c.date));
    const validMarkers = markers.filter(m => candleDates.has(m.time));
    if (validMarkers.length) series.setMarkers(validMarkers);

    // Cost-basis price lines for open stock positions on this symbol
    (positions || []).forEach(p => {
      const qty = Math.abs(parseFloat(p.quantity) || 0);
      const cost = parseFloat(p.cost_basis ?? p.costBasis ?? 0);
      const avg = parseFloat(p.averagePrice ?? p.average_price) || (qty > 0 ? Math.abs(cost) / qty : 0);
      if (!(avg > 0)) return;
      series.createPriceLine({
        price: avg,
        color: '#ed6c02',
        lineWidth: 1,
        lineStyle: 2, // dashed
        axisLabelVisible: true,
        title: 'basis',
      });
    });

    const onResize = () => chart.applyOptions({ width: el.clientWidth });
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      chart.remove();
      chartRef.current = null;
    };
  }, [candles, trades, positions, height, theme]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={interval}
          onChange={(_, v) => v && setInterval(v)}
        >
          <ToggleButton value="1d">Daily</ToggleButton>
          <ToggleButton value="1wk">Weekly</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={range}
          onChange={(_, v) => v && setRange(v)}
        >
          {RANGES.map(r => <ToggleButton key={r.key} value={r.key}>{r.label}</ToggleButton>)}
        </ToggleButtonGroup>
      </Box>
      <Box sx={{ position: 'relative', minHeight: height }}>
        {loading && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!loading && candles.length === 0 ? (
          <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" color="text.secondary">No price history available</Typography>
          </Box>
        ) : (
          <Box ref={containerRef} sx={{ height, width: '100%' }} />
        )}
      </Box>
    </Box>
  );
}
